import React, { useContext, useEffect, useMemo } from "react";
import { Navigate, useNavigate, useRouteError, useSearchParams } from "react-router";
import { ROUTES, routeWithParams } from "router";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import { AppContext } from "context/appContext";
import * as Sentry from "@sentry/react";
import AuthService from "utils/AuthService";
import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { AUTURA_ASPHALT_LIGHT, AUTURA_ASPHALT_LIGHTER } from "context/themeContext";

const TOKEN_EXPIRED_MESSAGE = "token is invalid";
const INVALID_TOKEN_MESSAGE = "invalid token specified";
const REGISTRATION_ERROR_MESSAGE = "registration error: ";

const ErrorPage = () => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const error = useRouteError();
  const navigate = useNavigate();
  const { state } = useContext(AppContext);

  const [searchParams] = useSearchParams();


  const errorMessage = useMemo(() => {
    if (searchParams.get("msg")) {
      return `${REGISTRATION_ERROR_MESSAGE} ${searchParams.get("msg")}`;
    }
    if (!error) return t("errors.unknown");
    if (error instanceof Error) {
      return error.message.toLowerCase().replace(/error:/g, "");
    } else if (error instanceof Response) {
      return `${error.body} - ${error.status}`;
    } else {
      try {
        return `${JSON.stringify(error)}`;
      } catch (e) {
        return t("errors.unknown");
      }
    }
  }, [error]);

  const handleError = (errorMessage: string) => {
    if (errorMessage.includes(TOKEN_EXPIRED_MESSAGE)) {
      displayToast({
        message: t("expired_token", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
      return <Navigate to={ROUTES.LOGIN} />;
    } else if (errorMessage.includes(INVALID_TOKEN_MESSAGE)) {
      displayToast({
        message: t("invalid_token", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
      return <Navigate to={ROUTES.LOGIN} />;
    } else if (errorMessage.includes("401") || errorMessage.includes("permissions")) {
      displayToast({
        message: t("unauthorized", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
    } else if (errorMessage.includes("403")) {
      displayToast({
        message: t("unauthorized", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
    } else if (
      errorMessage.includes("404") ||
      errorMessage.includes("not found")
    ) {
      displayToast({
        message: t("not_found", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
    } else if (errorMessage.includes(REGISTRATION_ERROR_MESSAGE)) {
      displayToast({
        message: 'nooooooo',
        type: TOAST_TYPE.ERROR,
      });
      AuthService.LogOut();
      return;
    } else {
      displayToast({
        message: t("errors.unknown", { ns: "toasts" }),
        type: TOAST_TYPE.ERROR,
      });
    }
    navigate(routeWithParams(ROUTES.DASHBOARD, [state.tenant.id]));
  };

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
    handleError(errorMessage);
  }, [errorMessage]);

  return <>
  {errorMessage.includes(REGISTRATION_ERROR_MESSAGE) && (
        <Card
        elevation={3}
        component="section"
        sx={{
          borderRadius: "0px",
          border: `1px solid ${AUTURA_ASPHALT_LIGHT}`,
          background: "#FFF",
          px: { xs: 1.5, sm: 4 },
          py: 4,
          position: "relative",
        }}
      >
          <CardHeader
            title="Registration Error"
            sx={{ p: 0, m: 0, mb: 3, width: "100%", zIndex: 2000 }}
            titleTypographyProps={{ variant: "h3", textTransform: "capitalize" }}
          />
          <CardContent
            sx={{
              p: 3,
              height: "100%",
              borderTop: `1px solid ${AUTURA_ASPHALT_LIGHTER}`,
            }}
          >
            <Typography variant={"h6"} textAlign={"center"} sx={{ mt: 4 }}>
              {errorMessage}
            </Typography>
          </CardContent>
        </Card>
  )}
  </>;
};

export default ErrorPage;
